import React, { useState, useEffect, useRef } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { CHAT_API_URL } from "../../../redux/constants/type";
import {
  UpdateWidgetConversation,
  getChatWidgetConversations,
} from "../../../redux/actions/chatActions";
import sendIcon from "../../../assets/icons/sendChat.svg";
import emojiIcon from "../../../assets/images/Group 354.svg";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import {
  Editor,
  EditorState,
  convertToRaw,
  convertFromRaw,
  Modifier,
  RichUtils,
  getDefaultKeyBinding,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { BsArrowDown } from "react-icons/bs";
import moment from "moment";

const getMessageId = (message) =>
  message?.messageid ?? message?.messageId ?? message?.MessageId ?? message?.id;

const getMessageSenderId = (message) =>
  message?.senderid ??
  message?.senderId ??
  message?.SenderId ??
  message?.userid ??
  message?.userId ??
  message?.UserId;

const getMessageGroupId = (message) =>
  message?.groupid ?? message?.groupId ?? message?.GroupId;

const areSameId = (firstId, secondId) =>
  firstId?.toString()?.toLowerCase() === secondId?.toString()?.toLowerCase();

const WidgetConversation = ({ currentUserId, groupIds, setToggle, token }) => {
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const [showDownArrow, setShowDownArrow] = useState(false);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [messagesList, setMessagesList] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [connection, setConnection] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [groupId, setGroupId] = useState(
    groupIds || window.localStorage.getItem("group-id")
  );
  const firstName = window.localStorage.getItem("firstname") || "";
  const addGroupsResponse = useSelector(
    (state) => state.chat.addGroupsWidgetResponse
  );
  const chatConversations = useSelector(
    (state) => state.chat.chatHistoryWidget
  );

  useEffect(() => {
    if (!groupId || !firstName || !currentUserId || !token) setToggle(false);
  }, [firstName, groupId, currentUserId, token, connection, messagesList]);

  // Function to clear local storage
  const clearLocalStorage = () => {
    window.localStorage.removeItem("group-id");
    window.localStorage.removeItem("firstname");
    window.localStorage.removeItem("userid");
    window.localStorage.removeItem("companyid");
    window.localStorage.removeItem("token");
  };

  // Function to reset the inactivity timer
  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    const newTimer = setTimeout(() => {
      clearLocalStorage();
      setToggle(false);
    }, 15 * 60 * 1000); // 15 minutes

    setInactivityTimer(newTimer);
  };

  useEffect(() => {
    resetInactivityTimer(); // Reset timer when component mounts
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (groupId && currentUserId) {
      dispatch(getChatWidgetConversations(groupId, currentUserId));
    }
  }, [groupId, dispatch, currentUserId]);

  useEffect(() => {
    if (addGroupsResponse?.status && currentUserId) {
      const newGroupId = addGroupsResponse?.data?.groupid;
      window.localStorage.setItem("group-id", newGroupId);
      setGroupId(newGroupId);
    }
  }, [addGroupsResponse]);

  const joinGroup = async (conn) => {
    try {
      if (groupId) {
        await conn.invoke("JoinGroup", `null-${groupId}`);
        if (currentUserId) {
          await conn
            .invoke("MessageReadStatusChanged", `null-${groupId}`, {
              userid: currentUserId,
              groupid: groupId,
            })
            .catch((err) => console.error(err));
        }
      }
      console.log("Joined group:", `${groupId}`);
    } catch (err) {
      console.error("Failed to join group:", err);
    }
  };

  useEffect(() => {
    if (chatConversations.length) {
      setMessagesList([...chatConversations.slice().reverse()]);
      scrollToBottom();
    }
  }, [chatConversations]);

  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(`${CHAT_API_URL}/chat`, {
        withCredentials: true,
        accessTokenFactory: () =>
          Promise.resolve(window.localStorage.getItem("token")),
      })
      .withAutomaticReconnect()
      .build();

    setConnection(newConnection);

    newConnection.onreconnected(async () => {
      console.log("Reconnected");
      await joinGroup(newConnection);
    });

    newConnection
      .start()
      .then(async () => {
        console.log("Connection started");
        await joinGroup(newConnection);
      })
      .catch(() => console.error("Connection failed "));
  }, []);

  useEffect(() => {
    if (connection) {
      connection.on("IsUserTyping", (isTyping) => {
        setIsTyping(isTyping);
      });
    }
  }, [connection]);

  useEffect(() => {
    if (!connection) return;

    const handleReceiveMessage = (message) => {
      setIsTyping(false);
      const messageId = getMessageId(message);
      const messageGroupId = getMessageGroupId(message) || groupId;
      const senderId = getMessageSenderId(message);
      let newMsg = {
        messageid: messageId,
        groupid: messageGroupId,
        senderid: senderId,
        messages: message?.messagetext,
        time: new Date(),
        type: message?.type,
        attachments: message?.attachments,
        senderimage: message?.senderimage,
        senderfirstname: message?.senderfirstname,
        senderlastname: message?.senderlastname,
      };
      dispatch(UpdateWidgetConversation(newMsg));
      if (
        messageId &&
        messageGroupId &&
        currentUserId &&
        !areSameId(senderId, currentUserId)
      ) {
        connection
          .invoke(
            "MessageDelivered",
            `null-${messageGroupId}`,
            messageId,
            currentUserId,
          )
          .catch((err) => console.error(err));
        connection
          .invoke("MessageReadStatusChanged", `null-${messageGroupId}`, {
            userid: currentUserId,
            groupid: messageGroupId,
          })
          .catch((err) => console.error(err));
      }
      scrollToBottom();
    };

    connection.on("ReceiveMessage", handleReceiveMessage);

    return () => {
      connection.off("ReceiveMessage", handleReceiveMessage);
    };
  }, [connection, currentUserId, dispatch, groupId]);

  const sendMessage = async () => {
    if (isSending) {
      // return;
    }
    const newState = RichUtils.toggleInlineStyle(editorState, "BOLD");
    const textMessage = convertToRaw(newState.getCurrentContent());
    const isEmptyMessage = textMessage.blocks[0].text.trim() === "";
    if (!isEmptyMessage) {
      resetInactivityTimer(); // Reset timer when a message is sent
      let messageId = uuidv4();
      setIsSending(true);
      const time = new Date().toISOString().split(".")[0];
      const chatMessage = {
        messageid: messageId,
        groupid: groupId,
        userid: currentUserId,
        messagetext: JSON.stringify(textMessage),
        attachments: "",
        type: "Group",
        senderimage: window.localStorage.getItem("userimageurl") ?? "",
        senderfirstname: window.localStorage.getItem("firstname") ?? "",
        senderlastname: window.localStorage.getItem("lastname") ?? "",
      };
      let newMsg = {
        messageid: messageId,
        senderid: chatMessage.userid,
        messages: chatMessage.messagetext,
        time: time,
        attachments: "",
        type: chatMessage.type,
        senderimage: window.localStorage.getItem("userimageurl") ?? "",
        senderfirstname: window.localStorage.getItem("firstname") ?? "",
        senderlastname: window.localStorage.getItem("lastname") ?? "",
      };
      if (navigator.onLine) {
        dispatch(UpdateWidgetConversation(newMsg));
      } else {
        dispatch(UpdateWidgetConversation(newMsg, "offline"));
        setEditorState(() => EditorState.createEmpty());
        scrollToBottom();
        return;
      }
      try {
        await connection.invoke("SendMessage", `null-${groupId}`, chatMessage);
        setEditorState(() => EditorState.createEmpty());
        scrollToBottom();
      } catch (err) {
        console.error("Failed to send message, attempting reconnect:", err);
        if (connection.state !== "Connected") {
          await connection.start();
          await joinGroup(connection);
          await connection.invoke(
            "SendMessage",
            `null-${groupId}`,
            chatMessage
          );
        }
      }
    }
    setIsSending(false);
  };

  function handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
      return "handled";
    } else {
      return getDefaultKeyBinding(event);
    }
  }

  const handleContainerScroll = (event) => {
    const scrollPosition = event.target.scrollTop;
    if (scrollPosition === 0 || (scrollPosition >= 0 && scrollPosition <= 2)) {
      setShowDownArrow(false);
    } else {
      setShowDownArrow(true);
    }
  };

  const scrollToBottom = () => {
    if (containerRef && containerRef.current) {
      const element = containerRef.current;
      element.scroll({
        top: element.scrollHeight,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const isJson = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const getFormattedText = (message) => {
    if (isJson(message.messages)) {
      return convertFromRaw(JSON.parse(message.messages)).getPlainText();
    }
    return message.messages;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "10px 20px",
        height: "100%",
        background: "#fff",
      }}
    >
      {messagesList && (
        <div
          className="chat-widget-list"
          ref={containerRef}
          onScroll={handleContainerScroll}
        >
          {messagesList.map((message, index) => {
            const reversedMessages = message?.messages?.slice().reverse();
            let previousSenderId = null;
            return (
              <div key={index}>
                {reversedMessages.map((msg, msgIndex) => {
                  const isMyMessage = msg?.senderid === currentUserId;
                  const shouldShowName = msg?.senderid !== previousSenderId;
                  previousSenderId = msg?.senderid;

                  return (
                    <div
                      key={msgIndex}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "3px",
                        marginBottom: "3px",
                        alignItems: isMyMessage ? "flex-end" : "start",
                      }}
                    >
                      {shouldShowName && (
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          {isMyMessage ? firstName : "Spiresolutions"}
                        </span>
                      )}
                      <div
                        style={{
                          background: isMyMessage
                            ? "#f1f1f1"
                            : "#D2DCE9 0% 0% no-repeat padding-box",
                          borderRadius: isMyMessage
                            ? "10px 0px 10px 10px"
                            : "0px 10px 10px 10px",
                          padding: "7px",
                          color: "#333333",
                          fontSize: "14px",
                          fontWeight: "normal",
                          wordWrap: "break-word",
                          width: "fit-content",
                          minWidth: "8rem",
                        }}
                      >
                        {getFormattedText(msg)}
                        <div className="message-time-style">
                          <span>{moment(msg?.time).format("hh:mm a")}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        {isTyping && (
          <span
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#333333",
              paddingLeft: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {"Spiresolutions"}
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "normal",
                  color: "#333333",
                  paddingLeft: "15px",
                }}
              >
                {"... is Typing"}
              </span>
            </div>
          </span>
        )}
        <div
          className="chat-widget-editor"
          style={{
            display: "flex",
            alignItems: "center",
            border: "0.5px solid #A5A5A5",
            borderRadius: "0px 10px 10px 10px",
            padding: "10px",
            background: "#FFF",
            position: "relative",
          }}
        >
          {showDownArrow && (
            <div className="chat-scroll-bottom" onClick={scrollToBottom}>
              <BsArrowDown color="#707070" />
            </div>
          )}
          <Editor
            editorState={editorState}
            onChange={setEditorState}
            handleReturn={(e) => {
              sendMessage();
              return "handled";
            }}
            placeholder="Type a message"
            keyBindingFn={handleKeyDown}
          />
          <div
            style={{
              position: "absolute",
              right: "10px",
              bottom: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <img
              src={emojiIcon}
              alt="emoji"
              style={{ cursor: "pointer", width: "24px", height: "24px" }}
              onClick={() => setShowPicker((prev) => !prev)}
            />
            <img
              src={sendIcon}
              alt="Send"
              style={{ cursor: "pointer", width: "24px", height: "24px" }}
              onClick={sendMessage}
            />
          </div>
          {showPicker && (
            <div
              style={{
                position: "absolute",
                bottom: "60px",
                right: "0px",
                zIndex: 10,
              }}
            >
              <Picker
                data={data}
                onEmojiSelect={(emoji) => {
                  const contentState = Modifier.insertText(
                    editorState.getCurrentContent(),
                    editorState.getSelection(),
                    emoji.native
                  );
                  const newEditorState = EditorState.push(
                    editorState,
                    contentState,
                    "insert-characters"
                  );
                  setEditorState(newEditorState);
                  setShowPicker(false);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetConversation;
